import React, { useEffect, useRef, useState } from 'react';
import {
  InputSwitch,
  type InputSwitchChangeEvent,
  type InputSwitchProps as PrimeInputSwitchProps,
} from 'primereact/inputswitch';
import styles from './ToggleSwitch.module.scss';

/**
 * ToggleSwitchProps defines the properties for the ToggleSwitch component.
 */
export interface ToggleSwitchProps {
  state: boolean;
  disabled?: boolean;
  isLabel?: boolean;
  label?: string;
  toggleSwitchId: string;
  name: string;
  onChange?: (checked: boolean) => void;
  inputSwitchProps?: Partial<PrimeInputSwitchProps>;
}

/**
 * ToggleSwitch component displays a customizable toggle switch with optional label.
 * @param {ToggleSwitchProps} props - The properties for the ToggleSwitch component.
 * @returns {JSX.Element} The rendered ToggleSwitch component.
 */

/**
 * Custom ToggleSwitch component using PrimeReact's InputSwitch. It supports True/False states, with options to control disabled state and label visibility.
 *
 * ## Import path
 * ```tsx
 * import { ToggleSwitch } from '@kpmg-global-audit-emu/react-ui-kit/atoms/ToggleSwitch';
 * ```
 * @param root0 - Component props
 * @param root0.state - Initial state of the switch
 * @param root0.disabled - Disables the switch when true
 * @param root0.isLabel - Displays a label if true
 * @param root0.label - The label text to display
 * @param root0.toggleSwitchId - The id for input switch
 * @param root0.name - The name for input switch
 * @param root0.onChange - Callback function when the switch state changes
 * @param root0.inputSwitchProps - Additional props to pass to the underlying PrimeReact InputSwitch component
 * @returns A styled toggle switch component
 */
export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  state,
  disabled = false,
  isLabel = false,
  label,
  toggleSwitchId,
  name,
  onChange,
  inputSwitchProps,
}) => {
  const [checked, setChecked] = useState(state);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setChecked(state);
  }, [state]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.id = toggleSwitchId;
    }
  }, [toggleSwitchId]);

  const handleChange = (e: InputSwitchChangeEvent) => {
    setChecked(e.value);
    if (onChange) {
      onChange(e.value); // ✅ notify parent component
    }
  };

  return (
    <div
      className={`${styles['toggle-switch-container']} ${disabled ? styles['disabled'] : ''} ${checked ? styles['checked'] : ''} ${isLabel ? '' : styles['no-label']}`}
    >
      <InputSwitch
        {...inputSwitchProps}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className={[
          styles['toggle-switch'],
          disabled ? styles['disabled'] : '',
          checked ? styles['checked'] : '',
          inputSwitchProps?.className,
        ]
          .filter(Boolean)
          .join(' ')}
        inputRef={inputRef}
        name={name}
      />
      {isLabel && <label htmlFor={toggleSwitchId}>{label || 'Text Label'}</label>}
    </div>
  );
};
